import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
});

// Uploaded files (staff photos, service/logo images) are served from the API
// origin's `/uploads/*` path, not under `/api` — build an absolute URL for them.
export const assetUrl = (path?: string | null): string | undefined => {
  if (!path) return undefined;
  if (/^https?:\/\//i.test(path)) return path;
  return `${API_BASE_URL.replace(/\/api\/?$/, '')}${path}`;
};

// ─── Request interceptor — attach token + branchId ────────────────────────────
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token    = localStorage.getItem('token');
  const branchId = localStorage.getItem('branchId');
  if (token)    config.headers.Authorization = `Bearer ${token}`;
  if (branchId) config.headers['X-Branch-ID'] = branchId;
  // Let the browser add the multipart boundary. Keeping the instance's
  // application/json default here can make Multer receive no file at all.
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    config.headers.delete('Content-Type');
  }
  return config;
});

// ─── Response interceptor — handle 401 ────────────────────────────────────────
// A 401 from /auth/login just means "wrong credentials" — that's a normal
// rejection the login form already surfaces, not an expired session. Only
// treat 401s from *authenticated* requests as a session-expiry logout, and
// only when there was actually a token to expire (avoids wiping/reloading
// while the user is simply sitting on the login page, logged out already).
api.interceptors.response.use(
  (res: AxiosResponse) => res,
  (err: AxiosError) => {
    const responseData = err.response?.data as { message?: unknown } | undefined;
    if (typeof responseData?.message === 'string' && responseData.message.trim()) {
      // Components that use Error.message now show the backend's actionable
      // upload validation instead of "Request failed with status code 400".
      err.message = responseData.message.trim();
    }
    const isLoginRequest = err.config?.url?.includes('/auth/login');
    if (err.response?.status === 401 && !isLoginRequest && localStorage.getItem('token')) {
      localStorage.removeItem('token');
      localStorage.removeItem('branchId');
      if (window.location.pathname !== '/login') window.location.href = '/login';
    }
    return Promise.reject(err);
  },
);

export default api;
